import React, { useState, useEffect } from 'react';
import { Plus, Scissors, Download, Edit } from 'lucide-react';
import { surgeryService } from '../../../lib/services.ipd';
import type { Surgery } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { generateSurgeryNotePDF } from '../../../lib/pdf';

interface SurgeryTabProps {
  admissionId: string;
}

export default function SurgeryTab({ admissionId }: SurgeryTabProps) {
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSurgeryForm, setShowSurgeryForm] = useState(false);
  const [editingSurgery, setEditingSurgery] = useState<Surgery | null>(null);
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    loadData();
  }, [admissionId]);

  const loadData = async () => {
    try {
      const data = await surgeryService.getByAdmission(admissionId);
      setSurgeries(data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load surgery data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      if (editingSurgery) {
        await surgeryService.update(editingSurgery.id, {
          ...data,
          scheduled_at: data.scheduled_at ? new Date(data.scheduled_at).toISOString() : undefined,
          started_at: data.started_at ? new Date(data.started_at).toISOString() : undefined,
          completed_at: data.completed_at ? new Date(data.completed_at).toISOString() : undefined,
        });
        toast.success('Surgery updated successfully');
      } else {
        await surgeryService.create({
          admission_id: admissionId,
          ...data,
          scheduled_at: data.scheduled_at ? new Date(data.scheduled_at).toISOString() : undefined,
          status: 'scheduled',
        });
        toast.success('Surgery scheduled successfully');
      }
      reset();
      setShowSurgeryForm(false);
      setEditingSurgery(null);
      loadData();
    } catch (error) {
      console.error('Failed to save surgery:', error);
      toast.error('Failed to save surgery');
    }
  };

  const completeSurgery = async (surgery: Surgery) => {
    try {
      await surgeryService.update(surgery.id, {
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
      toast.success('Surgery marked as completed');
      loadData();
    } catch (error) {
      console.error('Failed to complete surgery:', error);
      toast.error('Failed to complete surgery');
    }
  };

  const downloadSurgeryNote = (surgery: Surgery) => {
    const pdfData = {
      surgery_no: surgery.surgery_no || '',
      patient_name: 'Patient Name',
      admission_no: 'Admission No',
      surgeon_name: surgery.surgeon?.email || 'Surgeon',
      procedure_name: surgery.procedure_name,
      scheduled_at: surgery.scheduled_at || '',
      started_at: surgery.started_at,
      completed_at: surgery.completed_at,
      ot_room: surgery.ot_room,
      anesthesia_type: surgery.anesthesia_type,
      findings: surgery.findings,
      complications: surgery.complications,
      post_op_plan: surgery.post_op_plan,
    };

    const doc = generateSurgeryNotePDF(pdfData);
    doc.save(`surgery-note-${surgery.surgery_no}.pdf`);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Surgery / OT Notes</h3>
        <button
          onClick={() => {
            setShowSurgeryForm(!showSurgeryForm);
            setEditingSurgery(null);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Schedule Surgery
        </button>
      </div>

      {showSurgeryForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Procedure Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('procedure_name', { required: 'Procedure name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.procedure_name && <p className="text-sm text-red-600 mt-1">{errors.procedure_name.message as string}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date/Time</label>
              <input
                type="datetime-local"
                {...register('scheduled_at')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OT Room</label>
              <input
                type="text"
                {...register('ot_room')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anesthesia Type</label>
              <input
                type="text"
                {...register('anesthesia_type')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Cost</label>
              <input
                type="number"
                {...register('estimated_cost')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {editingSurgery && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operative Findings</label>
                <textarea
                  {...register('findings')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complications</label>
                <textarea
                  {...register('complications')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Post-operative Plan</label>
                <textarea
                  {...register('post_op_plan')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {editingSurgery ? 'Update Surgery' : 'Schedule Surgery'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowSurgeryForm(false);
                setEditingSurgery(null);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {surgeries.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No surgeries scheduled</p>
        ) : (
          surgeries.map((surgery) => (
            <div key={surgery.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Scissors className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">{surgery.procedure_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    surgery.status === 'completed' ? 'bg-green-100 text-green-800' :
                    surgery.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                    surgery.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {surgery.status}
                  </span>
                  {surgery.status !== 'completed' && surgery.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        setEditingSurgery(surgery);
                        setShowSurgeryForm(true);
                        Object.entries(surgery).forEach(([key, value]) => {
                          if (value) setValue(key, value);
                        });
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => downloadSurgeryNote(surgery)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">Surgery No: {surgery.surgery_no}</p>
              {surgery.scheduled_at && (
                <p className="text-sm text-gray-600">
                  Scheduled: {new Date(surgery.scheduled_at).toLocaleString()}
                </p>
              )}
              {surgery.ot_room && <p className="text-sm text-gray-600">OT Room: {surgery.ot_room}</p>}
              {surgery.status === 'scheduled' && (
                <button
                  onClick={() => completeSurgery(surgery)}
                  className="mt-2 text-sm text-green-600 hover:text-green-700"
                >
                  Mark as Completed
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
